// ** React Imports
import { ElementType, ReactNode } from 'react'

// ** Next Imports
import Link from 'next/link'
import { useRouter } from 'next/router'

// ** MUI Imports
import Chip from '@mui/material/Chip'
import ListItem from '@mui/material/ListItem'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Box, { BoxProps } from '@mui/material/Box'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemButton, { ListItemButtonProps } from '@mui/material/ListItemButton'

// ** Configs Import
import themeConfig from 'src/@core/configs/themeConfig'

// ** Types
import { NavLink } from 'src/@core/layouts/types'
import { Settings } from 'src/@core/context/settingsContext'

// ** Custom Components Imports
import UserIcon from 'src/layouts/components/UserIcon'

// ** Utils
import { handleURLQueries } from 'src/@core/layouts/utils'
import { Icon } from 'react-feather'
import { classNames } from 'src/lib/utils/helpers'

interface Props {
  item: NavLink
  settings: Settings
  navVisible?: boolean
  toggleNavVisibility: () => void
}

const VerticalNavLink = ({ item, navVisible, toggleNavVisibility }: Props) => {
  // ** Hooks
  const router = useRouter()
  const IconTag: string[] | ReactNode | Icon = item.icon
  const IconProp = item.iconProp ?? {}

  const isNavLinkActive = () => {
    const flowType = router.asPath.split(/[\/\?]/)
    if (flowType?.[1] === 'shipping-delivery' && item.path === '/shipping-delivery') {
      return true
    }
    if (flowType?.[2] === 'edit-c2p-discount' || flowType?.[2] === 'create-c2p-discount')
      return item.path === '/rto-suite'
    if (router.pathname === item.path || handleURLQueries(router, item.path)) {
      return true
    } else {
      return false
    }
  }

  return item.hidden ? (
    <></>
  ) : (
    <ListItem
      disablePadding
      className='nav-link'
      disabled={item.disabled || false}
      sx={{
        marginLeft: '2rem',
        width: '208px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0.2rem 0.1rem'
      }}
    >
      <Link
        href={item.path === undefined ? '/' : `${item.path}`}
        className={`
          w-full
          flex
          items-center
          gap-[0.2rem]
          rounded-2xl
          px-2
          py-[0.3rem]
          no-underline
          transition-opacity
          duration-250
          ${
            isNavLinkActive()
              ? 'border-2 border-[#F0F0F0] bg-gradient-to-l from-[#F0F0F0] to-[#FAFAFA]'
              : 'border-2 border-transparent'
          }
          hover:bg-[#F0F0F0]
          [&.active]:bg-[#F0F0F0]
          [&.active_.MuiTypography-root]:text-[text.header]
          [&.active_.MuiSvgIcon-root]:text-[text.header]
          [&:hover_.MuiTypography-root]:text-[text.header]
        `}
        {...(item.openInNewTab ? { target: '_blank' } : null)}
        onClick={e => {
          if (item.path === undefined) {
            e.preventDefault()
            e.stopPropagation()
          }
          if (navVisible) {
            toggleNavVisibility()
          }
        }}
      >
        <ListItemIcon
          sx={{
            mr: 1.5,
            height: '16px',
            width: '16px',
            display: 'flex',
            alignItems: 'center',
            color: isNavLinkActive() ? 'text.header' : 'text.secondary',
            transition: 'margin .25s ease-in-out'
          }}
        >
          <UserIcon icon={IconTag} iconProps={IconProp} />
        </ListItemIcon>

        <div
          className={`
          w-fit
          flex
          items-center
          justify-between
          transition-opacity
          duration-250
          ${themeConfig.menuTextTruncate ? 'overflow-hidden' : ''}
        `}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: isNavLinkActive() ? 'text.header' : 'text.secondary'
            }}
          >
            {item.title}
          </Typography>
          {item.badgeContent ? (
            <Chip
              label={item.badgeContent}
              color={item.badgeColor || 'primary'}
              sx={{
                height: 20,
                fontWeight: 500,
                marginLeft: 1.25,
                '& .MuiChip-label': { px: 1.5 }
              }}
            />
          ) : null}
        </div>
      </Link>
    </ListItem>
  )
}

export default VerticalNavLink
